// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import HeaderPanel from "@/UI/components/HeaderPanel";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    title: string;
    /** What this list adds up to — "14 companies · 9 subscribed". A count, not a description. */
    subtitle?: string;
    /** A note that belongs to the whole section rather than to a row: a deadline, a warning. */
    note?: ReactNode;
    /** The actions bar. It is the panel's last band rather than a separate card below it. */
    children?: ReactNode;
};

/**
 * The top of a **list** screen, the way `RecordPanel` is the top of a record screen: a name for what
 * you are looking at, what it adds up to, and the bar of tabs, filters and actions beneath it inside
 * one border.
 *
 * It exists because the four list screens in this app are the same screen four times. Left to
 * themselves each grew its own near-identical head and stylesheet, and the first change to the
 * subtitle would have had to be made four times and would have been made in two.
 */
export default function ListPanel({ title, subtitle, note, children }: Props) {
    return (
        <HeaderPanel
            head={
                <header className="list-panel__head">
                    <div className="list-panel__identity">
                        <h1 className="list-panel__title">{title}</h1>
                        {subtitle && <span className="list-panel__subtitle">{subtitle}</span>}
                    </div>
                    {note && <div className="list-panel__note">{note}</div>}
                </header>
            }
        >
            {children}
        </HeaderPanel>
    );
}
